import { useEffect, useState } from "react";

import useMutationObserver2 from "Utilities/useMutationObserver/useMutationObserver2";

const HOLDER_SELECTOR = ".signup_address_react_holder";

/**
 * The DOM holders the legacy Simple Signup page (`signupExtendedFields.js`)
 * drops in place of each Address field's widget, tagged with the field's
 * `networkFieldID`.
 *
 * @typedef {{ node: Element, networkFieldID: number }} AddressHolder
 */

const readHolders = () =>
  Array.from(document.querySelectorAll(HOLDER_SELECTOR)).map((node) => ({
    node,
    networkFieldID: Number(node.getAttribute("data-network-field-id")),
  }));

/**
 * Track every Address holder node on the Simple Signup / completeMyProfile page.
 *
 * The legacy fields render asynchronously (each waits on `loadAssets`), so the
 * holders appear after React mounts — a plain first-render query would miss
 * them. A MutationObserver on the body re-scans as nodes are added, the same
 * mechanism `usePortalElement` uses for a single node, generalized to the
 * multiple Address fields a network may configure.
 *
 * @returns {AddressHolder[]}
 */
export default function useAddressHolders() {
  const [holders, setHolders] = useState(readHolders);

  // A network can have zero Address fields; the initial state may be empty and
  // stay empty. Re-read once after mount to catch holders inserted between the
  // module-eval initial read and the observer being wired.
  useEffect(() => {
    setHolders((prev) => {
      const next = readHolders();
      return sameHolders(prev, next) ? prev : next;
    });
  }, []);

  useMutationObserver2({
    targetSelector: "body",
    handleMutation: () =>
      setHolders((prev) => {
        const next = readHolders();
        return sameHolders(prev, next) ? prev : next;
      }),
    options: { childList: true, subtree: true },
  });

  return holders;
}

// Identity by DOM node keeps the returned array referentially stable across
// unrelated body mutations, so consumers don't remount cards on every tick.
function sameHolders(a, b) {
  if (a.length !== b.length) return false;
  return a.every((holder, i) => holder.node === b[i].node);
}
